import { useEffect, useState } from 'react';
import { PageViewsType } from '../../../../store/models';
import { TopicMap } from '../../../../store/slices/topicSlice';
import { colors } from '../../../../theme/palette';
import { ChartContent } from './ChartContent';
import createStyles from '@material-ui/core/styles/createStyles';
import { Theme } from '@material-ui/core/styles/createTheme';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Typography, Box, Button, Tooltip } from '@mui/material';

export type TopicViewsCountProps = {
	data: PageViewsType[];
	topics: TopicMap;
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		bar: {
			height: 5,
			width: '100%',
		},
		viewBox: {
			width: '100%',
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			borderBottom: '1px solid #2C3F61',
		},
		labelBox: {
			display: 'flex',
			flexDirection: 'row',
			width: '100%',
			justifyContent: 'space-between',
		},
		infoBox: {
			display: 'flex',
			flexDirection: 'row',
			alignItems: 'center',
		},
		divider: {
			marginRight: 2,
			marginLeft: 2,
		},
		increase: {
			color: colors.greenDark,
		},
		decrease: {
			color: colors.redDark,
		},
	})
);

// a component named TopicViewsCount should be able to fetch its own data

export const TopicViewsCount = (props: TopicViewsCountProps) => {
	const { data, topics } = { ...props };
	const [pageNames, setPageNames] = useState({});
	const classes = useStyles();

	useEffect(() => {
		// if pageNames object isnt populated yet, but the topics object is, then populate pageNames
		if (!Object.keys(pageNames).length && Object.keys(topics).length) {
			const ids = Object.keys(topics);

			let tempPageNames = {};
			ids.forEach((id) => {
				// get the english name, or the first name if no english name
				tempPageNames[id] =
					topics[id]?.name?.find((lang) => lang.language === 'en')?.name || topics[id]?.name[0]?.name;
			});
			setPageNames(tempPageNames);
		}
	}, [topics, pageNames]);

	const [sortBy, setSortBy] = useState('views'); // 'views' or 'name'
	const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc'); // 'asc' or 'desc'

	const sortedTopics = [...(data || [])].sort((a, b) => {
		if (sortBy === 'views') {
			const aViews = a.views.reduce((sum, view) => sum + view.value, 0);
			const bViews = b.views.reduce((sum, view) => sum + view.value, 0);
			return sortOrder === 'desc' ? bViews - aViews : aViews - bViews;
		} else {
			const aName = pageNames[a.exhibitId] || '';
			const bName = pageNames[b.exhibitId] || '';
			return sortOrder === 'desc' ? bName.localeCompare(aName) : aName.localeCompare(bName);
		}
	});

	const handleSortViews = () => {
		if (sortBy === 'views') {
			setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
		}
		setSortBy('views');
	};

	const handleSortName = () => {
		if (sortBy === 'name') {
			setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
		}
		setSortBy('name');
	};

	return (
		<ChartContent
			name={'PAGE VIEWS - ALL'}
			label="VIEWS"
			toolTipMsg={'The number of views for all pages in your content.'}
			sticky={true}
		>
			<Box sx={{ display: 'flex', flexDirection: 'column', marginTop: '8px' }}>
				<>
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							marginTop: '8px',
						}}
					>
						<Tooltip title={'Sort'} placement={'top'} enterDelay={300} arrow>
							<Button
								size="small"
								onClick={handleSortName}
								sx={{ textTransform: 'none', color: colors.grayDark }}
							>
								Page Names {sortBy === 'name' && (sortOrder === 'desc' ? '↓' : '↑')}
							</Button>
						</Tooltip>
						<Tooltip title={'Sort'} placement={'top'} enterDelay={300} arrow>
							<Button
								size="small"
								onClick={handleSortViews}
								sx={{ textTransform: 'none', color: colors.grayDark }}
							>
								View Count {sortBy === 'views' && (sortOrder === 'desc' ? '↓' : '↑')}
							</Button>
						</Tooltip>
					</Box>

					{sortedTopics?.map(
						(dataPoint, index) =>
							pageNames[dataPoint.exhibitId] &&
							pageNames[dataPoint.exhibitId] !== '' &&
							dataPoint.exhibitId !== null && (
								<Box
									key={index}
									className={classes.viewBox}
									sx={{
										padding: '4px 8px',
										borderBottom: `1px solid ${colors.grayLight}`,
										'&:last-child': {
											borderBottom: 'none',
										},
									}}
								>
									<Box
										className={classes.labelBox}
										sx={{
											display: 'flex',
											justifyContent: 'space-between',
											alignItems: 'center',
										}}
									>
										<Typography variant="caption">{pageNames[dataPoint.exhibitId]}</Typography>

										<Box className={classes.infoBox}>
											<Typography variant="caption">
												{dataPoint?.views
													?.reduce((sum, view) => sum + view.value, 0)
													?.toLocaleString()}
											</Typography>
										</Box>
									</Box>
								</Box>
							)
					)}
				</>
			</Box>
		</ChartContent>
	);
};
